import { Command as CommandPrimitive } from 'cmdk'
import { SearchIcon } from 'lucide-react'
import type { ComponentProps } from 'react'

import { cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'

function Command({ className, ...props }: ComponentProps<typeof CommandPrimitive>) {
    return (
        <CommandPrimitive
            data-slot="command"
            className={cn(
                'flex size-full flex-col overflow-hidden rounded-lg bg-popover text-popover-foreground',
                className
            )}
            {...props}
        />
    )
}

function CommandDialog({
    title = 'Command palette',
    description = 'Search for a page, record, or action',
    children,
    className,
    ...props
}: ComponentProps<typeof Dialog> & { title?: string; description?: string; className?: string }) {
    return (
        <Dialog {...props}>
            <DialogContent
                showCloseButton={false}
                showGrabber={false}
                className={cn(
                    'overflow-hidden rounded-xl p-0 shadow-xl sm:max-w-xl',
                    // The palette is its own scroller, so it opts out of the dialog's mobile
                    // gutter padding at both widths.
                    'max-sm:p-0',
                    /*
                     * The one dialog that is deliberately not a bottom sheet. Its input takes
                     * focus the moment it opens, and an iOS software keyboard does not shrink
                     * the layout viewport a fixed element is positioned against, so a panel
                     * anchored to the bottom edge would open underneath the keyboard. It stays
                     * top-anchored and drops in from above; every class here answers one of the
                     * primitive's `max-sm:` sheet overrides.
                     */
                    'max-sm:bottom-auto max-sm:top-3 max-sm:w-[calc(100%-1.5rem)] max-sm:translate-y-0',
                    'max-sm:rounded-xl',
                    'max-sm:data-[state=open]:slide-in-from-top max-sm:data-[state=closed]:slide-out-to-top',
                    className
                )}>
                <div className="sr-only">
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>{description}</DialogDescription>
                </div>
                <Command
                    className={cn(
                        '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground',
                        '[&_[cmdk-group]]:px-1.5 [&_[cmdk-input-wrapper]_svg]:size-4'
                    )}>
                    {children}
                </Command>
            </DialogContent>
        </Dialog>
    )
}

function CommandInput({ className, ...props }: ComponentProps<typeof CommandPrimitive.Input>) {
    return (
        <div
            data-slot="command-input-wrapper"
            cmdk-input-wrapper=""
            className="flex h-12 shrink-0 items-center gap-3 border-b border-border px-4">
            <SearchIcon className="size-4 shrink-0 text-muted-foreground" />
            <CommandPrimitive.Input
                data-slot="command-input"
                className={cn(
                    'flex h-full w-full bg-transparent py-3 text-sm outline-none',
                    'placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
                    className
                )}
                {...props}
            />
        </div>
    )
}

function CommandList({ className, ...props }: ComponentProps<typeof CommandPrimitive.List>) {
    return (
        <CommandPrimitive.List
            data-slot="command-list"
            className={cn(
                // 20rem as today; below `sm` also capped against the visible viewport, which the
                // on-screen keyboard shrinks to roughly half the screen.
                'scrollbar-thin max-h-80 max-sm:max-h-[min(20rem,55dvh)] overflow-y-auto overflow-x-hidden py-2',
                className
            )}
            {...props}
        />
    )
}

function CommandEmpty({ className, ...props }: ComponentProps<typeof CommandPrimitive.Empty>) {
    return (
        <CommandPrimitive.Empty
            data-slot="command-empty"
            className={cn('py-10 text-center text-sm text-muted-foreground', className)}
            {...props}
        />
    )
}

function CommandGroup({ className, ...props }: ComponentProps<typeof CommandPrimitive.Group>) {
    return (
        <CommandPrimitive.Group
            data-slot="command-group"
            className={cn('overflow-hidden p-1 text-foreground', className)}
            {...props}
        />
    )
}

function CommandSeparator({ className, ...props }: ComponentProps<typeof CommandPrimitive.Separator>) {
    return (
        <CommandPrimitive.Separator
            data-slot="command-separator"
            className={cn('-mx-1 h-px bg-border', className)}
            {...props}
        />
    )
}

function CommandItem({ className, ...props }: ComponentProps<typeof CommandPrimitive.Item>) {
    return (
        <CommandPrimitive.Item
            data-slot="command-item"
            className={cn(
                // Thumb-sized rows on a phone; 36px stays the desktop density.
                'relative flex h-9 max-sm:h-11 cursor-default select-none items-center gap-2.5 rounded-md px-2 text-sm outline-none',
                'data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground',
                'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
                "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 [&_svg]:text-muted-foreground",
                className
            )}
            {...props}
        />
    )
}

function CommandShortcut({ className, ...props }: ComponentProps<'span'>) {
    return (
        <span
            data-slot="command-shortcut"
            className={cn('ml-auto text-2xs text-muted-foreground', className)}
            {...props}
        />
    )
}

export {
    Command,
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
    CommandShortcut
}
